import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { 
  ArrowRight,
  FileEdit,
  Zap,
  GitBranch,
  CheckCircle2,
  Clock,
  Bell,
  Tag,
  Download,
  Mail,
  MessageSquare,
  Users,
  Settings,
  LayoutDashboard,
  Filter,
  Search,
  BarChart3,
  Brain,
  Share2,
  Sparkles,
  Calendar,
  Send,
  Link as LinkIcon,
  FileCheck,
  Upload,
  ClipboardList,
  UserCheck,
  Briefcase,
  ExternalLink,
  AlertCircle,
  TrendingUp,
  XCircle,
  Webhook,
  Play
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "./LandingPage";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

// Mock UI Components for Screenshots
const MockPipelineView = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-zinc-50 px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Pipeline</h3>
          <p className="text-sm text-muted-foreground">Manage your leads and track conversions</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border">
            <Search className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Search leads...</span>
          </div>
          <Button variant="outline" size="sm" className="gap-2">
            <Filter className="w-4 h-4" /> Filter
          </Button>
        </div>
      </div>
    </div>
    
    {/* Stats Row */}
    <div className="grid grid-cols-5 gap-4 p-4 bg-[#fafaf8]">
      {[
        { label: "Total Leads", value: "24", color: "bg-blue-500" },
        { label: "New", value: "8", color: "bg-emerald-500" },
        { label: "Contacted", value: "6", color: "bg-yellow-500" },
        { label: "Qualified", value: "7", color: "bg-purple-500" },
        { label: "Won", value: "3", color: "bg-green-600" }
      ].map((stat) => (
        <div key={stat.label} className="bg-white rounded-lg p-3 border border-gray-100">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${stat.color}`} />
            <span className="text-2xl font-bold">{stat.value}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
        </div>
      ))}
    </div>
    
    {/* Lead Cards */}
    <div className="p-4 space-y-3">
      {[
        { name: "Sarah Chen", score: 92, status: "New", priority: "High", summary: "Senior VP at TechCorp seeking strategic consulting. $200K+ budget approved.", color: "bg-green-500" },
        { name: "Michael Rodriguez", score: 78, status: "Contacted", priority: "Medium", summary: "Founder of Series A startup looking for go-to-market strategy help.", color: "bg-yellow-500" },
        { name: "Jennifer Park", score: 85, status: "Qualified", priority: "High", summary: "Director of Operations at Fortune 500 looking for process optimization.", color: "bg-green-500" }
      ].map((lead, i) => (
        <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-lg border border-gray-100 hover:shadow-md transition-shadow cursor-pointer">
          <div className={`w-12 h-12 rounded-xl ${lead.color} text-white flex items-center justify-center text-lg font-bold`}>
            {lead.score}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold">{lead.name}</span>
              <Badge variant="outline" className={`text-xs ${lead.status === 'New' ? 'bg-blue-50 text-blue-700' : lead.status === 'Contacted' ? 'bg-yellow-50 text-yellow-700' : 'bg-purple-50 text-purple-700'}`}>
                {lead.status}
              </Badge>
              <Badge variant="outline" className={`text-xs ${lead.priority === 'High' ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'}`}>
                {lead.priority}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground truncate">{lead.summary}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MockFormBuilder = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-zinc-50 px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Form Builder</h3>
          <p className="text-sm text-muted-foreground">Customize your lead intake form</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">Preview</Button>
          <Button size="sm" className="bg-indigo-600">Save Changes</Button>
        </div>
      </div>
    </div>
    
    {/* Form Preview */}
    <div className="p-6">
      <div className="mb-4 flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Form Link:</span>
        <code className="bg-gray-100 px-2 py-1 rounded text-xs">heyintake.com/form/acme-consulting</code>
        <Button variant="ghost" size="sm" className="h-6 px-2">Copy</Button>
      </div>
      
      <div className="space-y-4">
        {[
          { label: "Full Name", type: "Text Input", required: true },
          { label: "Email", type: "Email", required: true },
          { label: "Company Name", type: "Text Input", required: true },
          { label: "What problem are you trying to solve?", type: "Long Text", required: true },
          { label: "Budget Range", type: "Dropdown", required: false }
        ].map((field, i) => (
          <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary/50 transition-colors cursor-pointer">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm">{field.label} {field.required && <span className="text-red-500">*</span>}</span>
              <Badge variant="outline" className="text-xs">{field.type}</Badge>
            </div>
            <div className="h-8 bg-white rounded border border-gray-200" />
          </div>
        ))}
        
        <Button variant="outline" className="w-full border-dashed">
          + Add Field
        </Button>
      </div>
    </div>
  </div>
);

const MockAIAnalysis = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 p-6">
    <div className="flex items-start gap-4">
      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-400 to-green-600 text-white flex items-center justify-center text-3xl font-bold shadow-lg">
        92
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-bold text-xl">Sarah Chen</h3>
          <Badge className="bg-red-100 text-red-700 border-red-200">Priority</Badge>
          <Badge className="bg-green-100 text-green-700 border-green-200">Enterprise</Badge>
        </div>
        
        <div className="bg-gradient-to-r from-primary/5 to-transparent p-4 rounded-lg mb-4 border-l-4 border-primary">
          <div className="flex items-center gap-2 mb-2">
            <Brain className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-semibold text-indigo-600">AI Summary</span>
          </div>
          <p className="text-sm text-muted-foreground">
            High-intent lead with clear budget ($200K+) and urgent timeline. Decision maker at mid-size company with full authority. Seeking strategic consulting for digital transformation. Q1 start required.
          </p>
        </div>
        
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-600" />
            <span>Respond within 24 hours</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span>High conversion probability</span>
          </div>
        </div>
      </div>
    </div>
    
    <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t">
      {[
        { label: "Budget", value: "$200K+", icon: "💰" },
        { label: "Timeline", value: "Q1 Start", icon: "📅" },
        { label: "Authority", value: "Decision Maker", icon: "👤" },
        { label: "Fit Score", value: "95%", icon: "🎯" }
      ].map((item) => (
        <div key={item.label} className="text-center p-3 bg-gray-50 rounded-lg">
          <span className="text-2xl">{item.icon}</span>
          <p className="font-semibold mt-1">{item.value}</p>
          <p className="text-xs text-muted-foreground">{item.label}</p>
        </div>
      ))}
    </div>
  </div>
);

const MockEmailOutreach = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-zinc-50 px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Email Lead</h3>
          <p className="text-sm text-muted-foreground">Send a personalized follow-up to Sarah Chen</p>
        </div>
        <Badge className="bg-green-100 text-green-700">Templates Available</Badge>
      </div>
    </div>
    
    <div className="p-6">
      {/* Template Selection */}
      <div className="mb-4">
        <label className="text-sm font-medium mb-2 block">Choose Template</label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { name: "Initial Outreach", desc: "First contact with lead" },
            { name: "Book a Call", desc: "Include your calendar link" }
          ].map((template, i) => (
            <div 
              key={i} 
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${i === 1 ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <p className="font-medium text-sm">{template.name}</p>
              <p className="text-xs text-muted-foreground">{template.desc}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Email Preview */}
      <div className="bg-gray-50 rounded-lg p-4 border">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b">
          <div className="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center">
            <Mail className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <p className="text-sm font-medium">To: sarah.chen@techcorp.com</p>
            <p className="text-xs text-muted-foreground">Subject: Let's schedule a discovery call</p>
          </div>
        </div>
        
        <div className="text-sm text-gray-700 space-y-3">
          <p>Hi Sarah,</p>
          <p>Thank you for your interest in our consulting services. Based on your digital transformation needs and Q1 timeline, I'd love to schedule a discovery call to discuss how we can help.</p>
          
          {/* Booking Link Highlight */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 my-3">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-indigo-600" />
              <span className="font-semibold text-indigo-600 text-sm">Book a Time That Works</span>
            </div>
            <a href="#" className="text-indigo-600 underline text-sm">calendly.com/acme-consulting/discovery</a>
          </div>
          
          <p>Looking forward to speaking with you!</p>
          <p className="text-gray-500">Best regards,<br/>John from Acme Consulting</p>
        </div>
      </div>
      
      {/* Send Button */}
      <div className="flex justify-end mt-4 gap-2">
        <Button variant="outline">Save as Draft</Button>
        <Button className="bg-indigo-600 gap-2">
          <Send className="w-4 h-4" />
          Send Email
        </Button>
      </div>
    </div>
  </div>
);

const MockDashboard = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-zinc-50 px-6 py-4 border-b border-gray-100">
      <h3 className="font-semibold text-lg">Welcome back, Acme Consulting</h3>
      <p className="text-sm text-muted-foreground">Here's an overview of your CRM activity</p>
    </div>
    
    <div className="p-6">
      {/* Getting Started */}
      <div className="mb-6 p-4 bg-gradient-to-r from-primary/10 to-transparent rounded-lg border border-indigo-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold">Getting Started</span>
          </div>
          <span className="text-sm text-indigo-600 font-medium">4 of 5 complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-indigo-600 h-2 rounded-full" style={{ width: '80%' }} />
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Leads", value: "24", change: "+12%", icon: Users },
          { label: "Conversion Rate", value: "32%", change: "+5%", icon: BarChart3 },
          { label: "Avg. Response Time", value: "2.4h", change: "-18%", icon: Clock }
        ].map((stat) => (
          <div key={stat.label} className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <stat.icon className="w-5 h-5 text-muted-foreground" />
              <span className="text-xs text-green-600 font-medium">{stat.change}</span>
            </div>
            <p className="text-2xl font-bold">{stat.value}</p>
            <p className="text-xs text-muted-foreground">{stat.label}</p>
          </div>
        ))}
      </div>
      
      {/* Recent Activity */}
      <div className="border-t pt-4">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" />
          Activity Timeline
        </h4>
        <div className="space-y-2">
          {[
            { action: "Intake form submitted by Sarah Chen", time: "2 min ago", type: "intake", emoji: "✅" },
            { action: "Lead stage: Qualified → Won", time: "1 hour ago", type: "status", emoji: "🎯" },
            { action: "Intake email sent to sarah@example.com", time: "1 hour ago", type: "email", emoji: "✉️" },
            { action: "New lead from intake form", time: "3 hours ago", type: "lead", emoji: "➕" }
          ].map((activity, i) => (
            <div key={i} className="flex items-center gap-3 text-sm p-2 hover:bg-gray-50 rounded">
              <span>{activity.emoji}</span>
              <span className="flex-1">{activity.action}</span>
              <span className="text-muted-foreground text-xs">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const MockIntakeForm = () => (
  <div className="bg-zinc-50 rounded-xl shadow-lg overflow-hidden border border-gray-200 p-8">
    <div className="text-center mb-8">
      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
        <Sparkles className="w-6 h-6 text-white" />
      </div>
      <h2 className="text-2xl font-bold">Acme Consulting</h2>
      <p className="text-muted-foreground">Let us know how we can help</p>
    </div>
    
    <div className="bg-white rounded-lg p-6 shadow-sm space-y-4 max-w-md mx-auto">
      {[
        { label: "Full Name", placeholder: "Your full name" },
        { label: "Email", placeholder: "you@company.com" },
        { label: "Company Name", placeholder: "Your company" },
        { label: "What problem are you trying to solve?", placeholder: "Describe your challenge...", multiline: true }
      ].map((field, i) => (
        <div key={i}>
          <label className="text-sm font-medium mb-1 block">{field.label} <span className="text-red-500">*</span></label>
          {field.multiline ? (
            <div className="h-20 bg-gray-50 rounded border border-gray-200" />
          ) : (
            <div className="h-10 bg-gray-50 rounded border border-gray-200" />
          )}
        </div>
      ))}
      <Button className="w-full mt-4">Submit</Button>
    </div>
  </div>
);

const MockAgreementSection = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-zinc-50 px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Agreement Acknowledgment</h3>
          <p className="text-sm text-muted-foreground">Require clients to review your terms before submitting</p>
        </div>
        <Badge className="bg-green-100 text-green-700">Active</Badge>
      </div>
    </div>
    
    <div className="p-6">
      {/* Agreement Preview */}
      <div className="p-4 bg-[#f8f8f5] rounded-lg border border-gray-200 mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-indigo-50">
            <FileCheck className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm mb-1">Service Agreement</p>
            <p className="text-xs text-muted-foreground mb-3">Clients will see a link to review your agreement before submitting.</p>
            <div className="flex items-center gap-2">
              <a href="#" className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                View Agreement PDF
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Client View Preview */}
      <div className="border-t pt-4">
        <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">What clients see:</p>
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-2">Please review our Service Agreement</p>
              <Button size="sm" variant="outline" className="bg-white mb-3 gap-2">
                <ExternalLink className="w-3 h-3" />
                Review Agreement
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2 border-blue-400 bg-white" />
                <span className="text-sm text-blue-800">I have reviewed and agree to the terms</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tracking Info */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">Tracking:</span> Agreement acknowledged status, timestamp, and link click tracking are saved per submission.
        </p>
      </div>
    </div>
  </div>
);

const MockDocumentUpload = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-zinc-50 px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Document Collection</h3>
          <p className="text-sm text-muted-foreground">Request files via external upload links</p>
        </div>
        <Badge className="bg-purple-100 text-purple-700">Flexible</Badge>
      </div>
    </div>
    
    <div className="p-6">
      {/* Upload Methods */}
      <div className="space-y-3 mb-6">
        <p className="text-sm font-medium text-foreground">Supported upload methods:</p>
        {[
          { name: "Dropbox File Request", icon: "📦", desc: "Share a file request link" },
          { name: "Google Drive Folder", icon: "📁", desc: "Share a folder for uploads" },
          { name: "Custom Upload Link", icon: "🔗", desc: "Any file sharing service" }
        ].map((method, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="text-xl">{method.icon}</span>
            <div>
              <p className="font-medium text-sm">{method.name}</p>
              <p className="text-xs text-muted-foreground">{method.desc}</p>
            </div>
          </div>
        ))}
      </div>
      
      {/* Client View Preview */}
      <div className="border-t pt-4">
        <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">What clients see:</p>
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
          <div className="flex items-start gap-3">
            <Upload className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-purple-900 mb-2">Please upload required documents</p>
              <p className="text-xs text-purple-700 mb-3">Click below to upload your files securely.</p>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 gap-2">
                <ExternalLink className="w-3 h-3" />
                Upload Documents
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Note */}
      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
        <p className="text-xs text-amber-800">
          <span className="font-medium">Note:</span> HeyIntake doesn't store uploaded files. Files are sent directly to your chosen storage provider.
        </p>
      </div>
    </div>
  </div>
);

const MockPostBookingIntake = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
      <div className="flex items-center gap-2 text-white">
        <UserCheck className="w-5 h-5" />
        <span className="font-semibold">Post-Booking Intake</span>
      </div>
      <p className="text-sm text-emerald-100 mt-1">For existing clients — collect project details after they've signed</p>
    </div>
    
    <div className="p-6">
      {/* Intake Status Pipeline */}
      <div className="mb-6">
        <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">Client Onboarding Pipeline</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-amber-100 text-amber-700 px-3 py-1">Intake Requested</Badge>
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          <Badge className="bg-blue-100 text-blue-700 px-3 py-1">Received</Badge>
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          <Badge className="bg-purple-100 text-purple-700 px-3 py-1">In Review</Badge>
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          <Badge className="bg-green-100 text-green-700 px-3 py-1">In Service</Badge>
        </div>
      </div>
      
      {/* AI Clarity Score */}
      <div className="p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg border-l-4 border-primary mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Brain className="w-4 h-4 text-indigo-600" />
          <span className="text-sm font-semibold text-indigo-600">AI Intake Clarity Score</span>
        </div>
        <p className="text-sm text-muted-foreground">
          AI analyzes completeness and identifies missing info, risk flags, and suggested follow-up questions.
        </p>
      </div>
      
      {/* Features List */}
      <div className="space-y-2">
        {[
          "Request project details, timelines, and requirements",
          "Agreement acknowledgment before submission",
          "AI-powered intake clarity scoring",
          "Automatic follow-up reminders for incomplete intakes",
          "Email notifications when intake is submitted"
        ].map((feature, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span className="text-muted-foreground">{feature}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const MockGlobalSearch = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-zinc-50 px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Global Search</h3>
          <p className="text-sm text-muted-foreground">Find any lead instantly across all data</p>
        </div>
      </div>
    </div>
    
    <div className="p-6">
      {/* Search Input */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <div className="w-full h-11 bg-gray-50 rounded-lg border border-gray-200 pl-10 flex items-center">
          <span className="text-muted-foreground">digital transformation</span>
        </div>
      </div>
      
      {/* Search Results */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">3 results found</p>
        {[
          { name: "Sarah Chen", match: "AI Summary: ...seeking digital transformation consulting...", score: 92 },
          { name: "TechCorp Inc", match: "Form answer: \"We need help with digital transformation\"", score: 78 },
          { name: "Jennifer Park", match: "Notes: Discussed digital transformation roadmap...", score: 85 }
        ].map((result, i) => (
          <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-primary/30 cursor-pointer transition-colors">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-sm">{result.name}</span>
              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">{result.score}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              <span className="bg-yellow-100 text-yellow-800 px-1 rounded">digital transformation</span>
              {result.match.replace("digital transformation", "")}
            </p>
          </div>
        ))}
      </div>
      
      {/* Search Scope */}
      <div className="mt-4 pt-4 border-t">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">Searches across:</span> Names, emails, form answers, AI summaries, notes, and custom fields
        </p>
      </div>
    </div>
  </div>
);

const MockAnalytics = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-zinc-50 px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Analytics</h3>
          <p className="text-sm text-muted-foreground">Track performance and identify trends</p>
        </div>
        <Badge className="bg-indigo-50 text-indigo-600">Last 30 days</Badge>
      </div>
    </div>
    
    <div className="p-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Leads", value: "127", change: "+23%", up: true },
          { label: "Conversion Rate", value: "34%", change: "+8%", up: true },
          { label: "Avg. Score", value: "72", change: "+5", up: true },
          { label: "Intake Complete", value: "89%", change: "+12%", up: true }
        ].map((metric) => (
          <div key={metric.label} className="p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold">{metric.value}</p>
            <p className="text-xs text-muted-foreground">{metric.label}</p>
            <p className={`text-xs mt-1 ${metric.up ? 'text-green-600' : 'text-red-600'}`}>
              {metric.up ? '↑' : '↓'} {metric.change}
            </p>
          </div>
        ))}
      </div>
      
      {/* Pipeline Breakdown */}
      <div className="mb-6">
        <p className="text-sm font-medium mb-3">Pipeline Breakdown</p>
        <div className="h-8 bg-gray-100 rounded-lg overflow-hidden flex">
          <div className="bg-blue-500 h-full" style={{ width: '30%' }} />
          <div className="bg-yellow-500 h-full" style={{ width: '25%' }} />
          <div className="bg-purple-500 h-full" style={{ width: '20%' }} />
          <div className="bg-green-500 h-full" style={{ width: '15%' }} />
          <div className="bg-red-400 h-full" style={{ width: '10%' }} />
        </div>
        <div className="flex justify-between mt-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><div className="w-2 h-2 bg-blue-500 rounded" /> New (30%)</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 bg-yellow-500 rounded" /> Contacted (25%)</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 bg-purple-500 rounded" /> Qualified (20%)</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 bg-green-500 rounded" /> Won (15%)</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 bg-red-400 rounded" /> Lost (10%)</span>
        </div>
      </div>
      
      {/* Lost Reasons */}
      <div className="p-4 bg-red-50 rounded-lg border border-red-100">
        <p className="text-sm font-medium text-red-900 mb-2 flex items-center gap-2">
          <XCircle className="w-4 h-4" /> Top Lost Reasons
        </p>
        <div className="space-y-2">
          {[
            { reason: "Budget constraints", count: 5 },
            { reason: "Chose competitor", count: 3 },
            { reason: "Timeline mismatch", count: 2 }
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-red-800">{item.reason}</span>
              <Badge variant="outline" className="text-xs bg-white">{item.count}</Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const MockAutomations = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-zinc-50 px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Automations</h3>
          <p className="text-sm text-muted-foreground">Set it and forget it workflows</p>
        </div>
        <Button size="sm" className="gap-2">
          <Play className="w-3 h-3" /> New Automation
        </Button>
      </div>
    </div>
    
    <div className="p-6 space-y-4">
      {/* Automation 1 */}
      <div className="p-4 bg-green-50 rounded-lg border border-green-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="font-medium text-sm">Welcome New Leads</span>
          </div>
          <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="bg-white px-2 py-1 rounded border">Form Submitted</span>
          <ArrowRight className="w-3 h-3" />
          <span className="bg-white px-2 py-1 rounded border">Send Welcome Email</span>
        </div>
        <p className="text-xs text-green-700 mt-2">Triggered 47 times this month</p>
      </div>
      
      {/* Automation 2 */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
            <span className="font-medium text-sm">Request Intake on Won</span>
          </div>
          <Badge className="bg-blue-100 text-blue-700 text-xs">Active</Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="bg-white px-2 py-1 rounded border">Status → Won</span>
          <ArrowRight className="w-3 h-3" />
          <span className="bg-white px-2 py-1 rounded border">Send Intake Form</span>
        </div>
        <p className="text-xs text-blue-700 mt-2">Triggered 12 times this month</p>
      </div>
      
      {/* Automation 3 */}
      <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
            <span className="font-medium text-sm">Qualify High Scorers</span>
          </div>
          <Badge className="bg-purple-100 text-purple-700 text-xs">Active</Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="bg-white px-2 py-1 rounded border">Score ≥ 80</span>
          <ArrowRight className="w-3 h-3" />
          <span className="bg-white px-2 py-1 rounded border">Set Status: Qualified</span>
        </div>
        <p className="text-xs text-purple-700 mt-2">Triggered 23 times this month</p>
      </div>
    </div>
  </div>
);

const MockZapierIntegration = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
    {/* Header */}
    <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
      <div className="flex items-center gap-3 text-white">
        <img src="https://cdn.simpleicons.org/zapier/ffffff" alt="Zapier" className="w-6 h-6" />
        <div>
          <h3 className="font-semibold text-lg">Zapier Integration</h3>
          <p className="text-sm text-orange-100">Connect to 5,000+ apps</p>
        </div>
      </div>
    </div>
    
    <div className="p-6">
      {/* Webhook Events */}
      <div className="mb-6">
        <p className="text-sm font-medium mb-3">Webhook Events</p>
        <div className="space-y-2">
          {[
            { event: "submission.created", desc: "When a new lead submits a form", active: true },
            { event: "submission.status_changed", desc: "When lead status is updated", active: true },
            { event: "submission.intake_completed", desc: "When client completes intake", active: true }
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <code className="text-xs bg-gray-200 px-2 py-0.5 rounded">{item.event}</code>
                <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
              </div>
              <div className={`w-3 h-3 rounded-full ${item.active ? 'bg-green-500' : 'bg-gray-300'}`} />
            </div>
          ))}
        </div>
      </div>
      
      {/* Example Use Cases */}
      <div className="border-t pt-4">
        <p className="text-sm font-medium mb-3">Popular Automations</p>
        <div className="grid grid-cols-2 gap-2">
          {[
            "Add leads to Google Sheets",
            "Create Slack notifications",
            "Sync to your CRM",
            "Send SMS via Twilio"
          ].map((useCase, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground p-2 bg-orange-50 rounded">
              <Zap className="w-3 h-3 text-orange-500" />
              {useCase}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default function DemoPage() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter" }}>
      <MarketingNav />

      <section className="pt-36 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-indigo-600 mb-4">PRODUCT TOUR</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-zinc-950 tracking-tight leading-tight mb-6" style={{ fontFamily: "Inter" }}>See HeyIntake in Action</h1>
            <p className="text-lg text-zinc-500 leading-relaxed max-w-2xl mx-auto">
              From lead capture to client onboarding — one intelligent platform for service businesses. AI scoring, CRM, agreements, and document collection all in one place.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 px-6 bg-zinc-50">
        <div className="max-w-6xl mx-auto">
          
          {/* Step 1: Create Form */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">1</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step One</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Create Your Intake Form
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Build custom intake forms in minutes with our drag-and-drop builder. 
                  Collect exactly the information your business needs to qualify leads.
                </p>
                <ul className="space-y-3">
                  {[
                    "Customizable fields for any business type",
                    "Shareable link for your website or email",
                    "AI-optimized questions for better scoring",
                    "Mobile-responsive design"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:pl-8">
                <MockFormBuilder />
              </div>
            </div>
          </motion.div>

          {/* Step 2: AI Scoring */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">2</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step Two</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  AI Scores Every Submission
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Every submission is automatically analyzed by AI to give you instant insights.
                  Know immediately which leads deserve priority attention.
                </p>
                <ul className="space-y-3">
                  {[
                    "Lead score from 0-100 based on fit",
                    "Priority labels (High, Medium, Low)",
                    "Intelligent summary of key details",
                    "Recommended follow-up timing"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:order-1 lg:pr-8">
                <MockAIAnalysis />
              </div>
            </div>
          </motion.div>

          {/* Step 3: Pipeline Management */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">3</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step Three</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Manage in Your Pipeline
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Track every lead from first touch to closed deal. 
                  Built-in CRM features mean you don't need separate tools.
                </p>
                
                {/* Pipeline Status Visual */}
                <div className="p-4 bg-white rounded-xl shadow-sm mb-6">
                  <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">Unified Lead → Client Journey</p>
                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    <Badge className="bg-blue-100 text-blue-700 px-3 py-1">New</Badge>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <Badge className="bg-yellow-100 text-yellow-700 px-3 py-1">Contacted</Badge>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <Badge className="bg-purple-100 text-purple-700 px-3 py-1">Qualified</Badge>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <Badge className="bg-green-100 text-green-700 px-3 py-1">Won</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">When Won, seamlessly start intake on the same record:</p>
                  <div className="flex items-center gap-2 flex-wrap bg-gray-50 p-2 rounded-lg">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">Lead:</span>
                      <Badge className="bg-green-100 text-green-700 text-xs">Won</Badge>
                    </div>
                    <span className="text-gray-400">+</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">Intake:</span>
                      <Badge className="bg-slate-100 text-slate-700 text-xs">Requested</Badge>
                    </div>
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                    <Badge className="bg-blue-100 text-blue-700 text-xs">Received</Badge>
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                    <Badge className="bg-indigo-100 text-indigo-700 text-xs">In Service</Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: MessageSquare, label: "Add notes" },
                    { icon: Bell, label: "Set reminders" },
                    { icon: Tag, label: "Tag leads" },
                    { icon: Clock, label: "Activity timeline" }
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2 text-muted-foreground text-sm">
                      <item.icon className="w-4 h-4 text-indigo-600" />
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:pl-8">
                <MockPipelineView />
              </div>
            </div>
          </motion.div>

          {/* Step 4: Email Outreach */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">4</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step Four</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Reach Out with Templates
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Email leads directly from HeyIntake using customizable templates.
                  Include your booking links to streamline the scheduling process.
                </p>
                <ul className="space-y-3">
                  {[
                    "Pre-built email templates for common scenarios",
                    "Include your Calendly or booking link",
                    "Personalized with lead details automatically",
                    "Track email history per lead"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                {/* Booking Link Tip */}
                <div className="mt-6 p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                  <div className="flex items-center gap-2 mb-2">
                    <LinkIcon className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-semibold text-indigo-600">Pro Tip</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Add your Calendly or booking link in Settings to automatically include it in email templates.
                  </p>
                </div>
              </div>
              <div className="lg:order-1 lg:pr-8">
                <MockEmailOutreach />
              </div>
            </div>
          </motion.div>

          {/* Step 5: Dashboard */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">5</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step Five</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Monitor Performance
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Get a bird's eye view of your lead intake with the dashboard.
                  Track key metrics and never miss a follow-up.
                </p>
                <ul className="space-y-3">
                  {[
                    "Real-time lead tracking",
                    "Conversion rate analytics",
                    "Response time monitoring",
                    "Activity feed and reminders"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:pl-8">
                <MockDashboard />
              </div>
            </div>
          </motion.div>

          {/* Step 6: Client Agreements */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">6</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step Six</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Get Agreements Acknowledged
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Require clients to review and acknowledge your service agreement, terms, or contract 
                  before submitting their intake form.
                </p>
                <ul className="space-y-3">
                  {[
                    "Link to your existing agreement or contract",
                    "Clients must acknowledge before submitting",
                    "Track who viewed and signed",
                    "Timestamped acknowledgment for your records"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:order-1 lg:pr-8">
                <MockAgreementSection />
              </div>
            </div>
          </motion.div>

          {/* Step 7: Document Collection */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">7</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step Seven</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Collect Documents Easily
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Request files from clients using your preferred cloud storage — 
                  Dropbox, Google Drive, or any file sharing service.
                </p>
                <ul className="space-y-3">
                  {[
                    "Works with any file sharing platform",
                    "Clients upload directly to your storage",
                    "Configure per form — optional or required",
                    "Instructions displayed clearly to clients"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:pl-8">
                <MockDocumentUpload />
              </div>
            </div>
          </motion.div>

          {/* Step 8: Post-Booking Intake */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">8</div>
                  <span className="text-sm font-medium text-emerald-600 uppercase tracking-wider">Bonus: Client Onboarding</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Onboard Clients After They Sign
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Once a lead becomes a client, switch to post-booking mode. 
                  Collect project details, requirements, and preferences for a smooth kickoff.
                </p>
                <ul className="space-y-3">
                  {[
                    "Dedicated pipeline for client onboarding",
                    "AI intake clarity scoring for completeness",
                    "Request agreements and documents",
                    "Automated reminders for incomplete intakes",
                    "Separate from lead qualification workflow"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:order-1 lg:pr-8">
                <MockPostBookingIntake />
              </div>
            </div>
          </motion.div>

          {/* Step 9: Global Search */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">9</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step Nine</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Find Any Lead Instantly
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Powerful global search across all your data — names, emails, form answers, 
                  AI summaries, notes, and custom fields. Find what you need in seconds.
                </p>
                <ul className="space-y-3">
                  {[
                    "Search across all submissions instantly",
                    "Includes AI summaries and form answers",
                    "Highlights matching text in results",
                    "Works with custom fields and notes"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:pl-8">
                <MockGlobalSearch />
              </div>
            </div>
          </motion.div>

          {/* Step 10: Analytics & Lost Reasons */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">10</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step Ten</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Measure What Matters
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Track your conversion rates, pipeline health, and understand why leads don't convert.
                  Make data-driven decisions to improve your business.
                </p>
                <ul className="space-y-3">
                  {[
                    "Real-time conversion and pipeline metrics",
                    "Track lost reasons to identify patterns",
                    "Average lead score trends over time",
                    "Intake completion rate monitoring"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                {/* Lost Reason Tip */}
                <div className="mt-6 p-4 bg-red-50 rounded-xl border border-red-100">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-semibold text-red-700">Lost Reason Tracking</span>
                  </div>
                  <p className="text-sm text-red-600">
                    When marking a lead as lost, record why. Over time, spot patterns and fix leaks in your funnel.
                  </p>
                </div>
              </div>
              <div className="lg:order-1 lg:pr-8">
                <MockAnalytics />
              </div>
            </div>
          </motion.div>

          {/* Step 11: Automations */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">11</div>
                  <span className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Step Eleven</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Automate Repetitive Tasks
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Set up workflows that run automatically when leads submit forms or change status.
                  Save hours every week on manual follow-ups.
                </p>
                <ul className="space-y-3">
                  {[
                    "Trigger on form submission or status change",
                    "Auto-send emails using your templates",
                    "Automatically send intake forms to new clients",
                    "Update status based on lead score",
                    "Pre-built templates to get started fast"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:pl-8">
                <MockAutomations />
              </div>
            </div>
          </motion.div>

          {/* Step 12: Zapier Integration */}
          <motion.div className="mb-24" {...fadeIn}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">12</div>
                  <span className="text-sm font-medium text-orange-600 uppercase tracking-wider">Integrations</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Connect to 5,000+ Apps
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Send your lead data anywhere with Zapier webhooks. Sync to your CRM, 
                  spreadsheets, Slack, or any tool in your stack.
                </p>
                <ul className="space-y-3">
                  {[
                    "Real-time webhooks on key events",
                    "New submission, status change, intake complete",
                    "Flat JSON payload for easy mapping",
                    "Test webhooks before going live"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:order-1 lg:pr-8">
                <MockZapierIntegration />
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Complete Feature Set</h2>
            <p className="text-muted-foreground">Everything service businesses need for lead capture and client onboarding</p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              { icon: Brain, title: "AI Lead Scoring", desc: "Automatic 0-100 scoring based on submission content" },
              { icon: Briefcase, title: "Built-in CRM", desc: "Pipeline tracking, notes, tags, and status updates" },
              { icon: Search, title: "Global Search", desc: "Find any lead instantly across all data" },
              { icon: BarChart3, title: "Analytics Dashboard", desc: "Track conversions, pipeline health, and trends" },
              { icon: ClipboardList, title: "Structured Intake", desc: "Custom forms for any business type" },
              { icon: UserCheck, title: "Client Onboarding", desc: "Post-booking intake for project kickoff" },
              { icon: Zap, title: "Workflow Automations", desc: "Auto-send emails and update status on triggers" },
              { icon: Webhook, title: "Zapier Webhooks", desc: "Connect to 5,000+ apps via webhooks" },
              { icon: FileCheck, title: "Agreement Signing", desc: "Require acknowledgment of terms and contracts" },
              { icon: Upload, title: "Document Collection", desc: "Request files via Dropbox, Drive, or any link" },
              { icon: XCircle, title: "Lost Reason Tracking", desc: "Understand why leads don't convert" },
              { icon: Mail, title: "Email Templates", desc: "Customizable templates with variables" },
              { icon: Bell, title: "Smart Reminders", desc: "Automated follow-up reminders" },
              { icon: Users, title: "Team Management", desc: "Role-based access for your team" },
              { icon: Download, title: "CSV Export", desc: "Export all data including agreement status" },
              { icon: Settings, title: "White-label Option", desc: "Custom branding and colors" }
            ].map((feature) => (
              <Card key={feature.title} className="p-5 border-none shadow-sm hover:shadow-md transition-shadow">
                <feature.icon className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="font-semibold mb-1 text-sm">{feature.title}</h3>
                <p className="text-xs text-muted-foreground">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeIn}>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "Inter" }}>Ready to see it yourself?</h2>
            <p className="text-lg text-zinc-400 mb-8">Start your 7-day trial. No commitment required.</p>
            <Link to="/register"><Button className="h-14 px-10 text-base rounded-full bg-indigo-600 text-white hover:bg-indigo-700">Start Trial <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
            <p className="text-sm text-zinc-500 mt-4">Includes up to 20 submissions. Cancel anytime.</p>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
